import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail, isNetworkError } from "../../lib/api";
import { daysUntil } from "../../components/ExpirationBadge";
import { ProductImage } from "../../components/ProductImage";
import { toast } from "sonner";
import { Minus, Plus, Trash2, X, AlertTriangle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  saveInventory,
  readInventory,
  enqueueOutbox,
  listOutbox,
  isOnline,
} from "../../lib/offlineStore";
import syncRunner from "../../lib/syncRunner";
import OfflineUnavailable, { CachedDataNotice } from "../../components/system/OfflineUnavailable";
import SuggestionsTrigger from "../../components/SuggestionsTrigger";

// Urgency metadata is kept here so colors/labels stay aligned with the rest
// of the app, but the page now renders a single flat list (filtered) instead
// of stacked collapsible sections.
const URGENCY_META = {
  expired:   { label: "Expired",        hint: "Past date",   text: "text-shelf-expired",   bg: "bg-shelf-expired-bg",   bar: "bg-shelf-expired",   defaultReason: "expired" },
  urgent:    { label: "Urgent",         hint: "≤ 1 day",     text: "text-shelf-urgent",    bg: "bg-shelf-urgent-bg",    bar: "bg-shelf-urgent",    defaultReason: "near_expiration" },
  attention: { label: "Needs attention", hint: "≤ 7 days",    text: "text-shelf-attention", bg: "bg-shelf-attention-bg", bar: "bg-shelf-attention", defaultReason: "near_expiration" },
  fresh:     { label: "Fresh",          hint: "> 7 days",    text: "text-shelf-fresh",     bg: "bg-shelf-fresh-bg",     bar: "bg-shelf-fresh",     defaultReason: "damaged" },
};
const URGENCY_ORDER = ["expired", "urgent", "attention", "fresh"];
const FILTERS = [
  { key: "all",       label: "All statuses" },
  { key: "expired",   label: "Expired" },
  { key: "urgent",    label: "Urgent" },
  { key: "attention", label: "Needs attention" },
  { key: "fresh",     label: "Fresh" },
];
// When viewing "All statuses" we cap each section to keep the screen
// scannable on a phone; when filtering to a specific status, show every
// matching batch so employees can clear the whole list.
const ALL_VIEW_LIMIT_PER_STATUS = 25;

function relDays(iso) {
  const d = daysUntil(iso);
  if (d === null || Number.isNaN(d)) return iso;
  if (d < 0) return `${Math.abs(d)}d ago`;
  if (d === 0) return "today";
  if (d === 1) return "tomorrow";
  return `in ${d}d`;
}

function formatShortDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
}

function StatusPill({ urgency }) {
  const m = URGENCY_META[urgency];
  if (!m) return null;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider ${m.bg} ${m.text}`}
      data-testid={`status-pill-${urgency}`}
    >
      {m.label}
    </span>
  );
}

function InventoryRow({ batch, selected, onToggle, onRemoved, onEnqueueRemove, onLocalApplyRemove }) {
  const initial = Math.min(1, batch.quantity);
  const [qty, setQty] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const meta = URGENCY_META[batch.urgency] || URGENCY_META.fresh;
  const isPendingRemove = batch._pendingRemove === true;
  const isPendingAdd = batch._pendingAdd === true;
  const isError = batch._syncError === true;

  const dec = () => setQty((q) => Math.max(1, q - 1));
  const inc = () => setQty((q) => Math.min(batch.quantity, q + 1));
  const setAll = () => setQty(batch.quantity);

  const doRemove = async () => {
    if (submitting || isPendingRemove) return;
    setSubmitting(true);
    const body = { quantity: Number(qty), reason: meta.defaultReason, note: null };
    // Offline-first: enqueue when offline; on online-but-network-fail,
    // enqueue too (the live POST didn't reach the server, so it's safe
    // to queue and replay).
    if (!isOnline()) {
      try {
        await onEnqueueRemove(batch.id, body);
        toast.success(`Queued: remove ${qty} × ${batch.product_name || "item"}`);
      } finally { setSubmitting(false); }
      return;
    }
    try {
      await api.post(`/inventory/${batch.id}/remove`, body);
      const remaining = Math.max(0, (Number(batch.quantity) || 0) - Number(qty));
      const productName = batch.product_name || "item";
      toast.success(
        remaining === 0
          ? `Removed final ${productName}`
          : `Removed ${qty} × ${productName} (${remaining} left)`
      );
      // Patch the local in-memory + IndexedDB snapshot BEFORE asking the
      // server for a fresh list. This makes the UI honest even if the
      // post-action GET briefly fails: the cache will already reflect the
      // removal we just did, so it can't resurrect the row.
      try { onLocalApplyRemove && onLocalApplyRemove(batch.id, Number(qty)); } catch (_) { /* never block */ }
      onRemoved();
    } catch (e) {
      if (!e?.response) {
        await onEnqueueRemove(batch.id, body);
        toast.success(`Queued: remove ${qty} × ${batch.product_name || "item"}`);
      } else {
        toast.error(formatApiErrorDetail(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`flex items-center gap-3 px-3 py-4 border-b border-border last:border-b-0 hover:bg-secondary/40 active:bg-secondary/60 transition-colors ${isPendingRemove || isError ? "opacity-70" : ""}`}
      data-testid={`inventory-row-${batch.id}`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onToggle(batch.id)}
        disabled={isPendingRemove || isPendingAdd}
        className="w-5 h-5 accent-brand cursor-pointer shrink-0 disabled:opacity-40"
        data-testid={`inventory-checkbox-${batch.id}`}
        aria-label={`Select ${batch.product_name || batch.id}`}
      />
      <ProductImage src={batch.product_image_url} alt={batch.product_name || ""} size="w-12 h-12" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          <StatusPill urgency={batch.urgency} />
          {isPendingAdd && (
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink/60 bg-secondary px-1.5 rounded-sm" data-testid={`row-pending-add-${batch.id}`}>
              Pending sync (add)
            </span>
          )}
          {isPendingRemove && (
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink/60 bg-secondary px-1.5 rounded-sm" data-testid={`row-pending-remove-${batch.id}`}>
              Pending sync (remove)
            </span>
          )}
          {isError && (
            <span className="font-mono text-[10px] uppercase tracking-wider text-shelf-expired bg-shelf-expired-bg px-1.5 rounded-sm" data-testid={`row-sync-error-${batch.id}`}>
              Sync error
            </span>
          )}
        </div>
        <div className="font-display font-semibold text-sm truncate leading-snug" data-testid={`inventory-name-${batch.id}`}>
          {batch.product_name || "—"}
        </div>
        <div className="font-mono text-[11px] text-muted-foreground mt-0.5 truncate">
          <span className="text-ink/80 font-semibold">{batch.quantity}</span>
          <span className="opacity-60"> · </span>
          {formatShortDate(batch.expiration_date)}
          <span className="opacity-60"> · </span>
          <span className="opacity-80">{relDays(batch.expiration_date)}</span>
        </div>
        {!isPendingAdd && !isPendingRemove && (
          <div className="mt-1.5">
            <SuggestionsTrigger batch={batch} />
          </div>
        )}
      </div>

      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <div className="flex items-center border border-ink/70 rounded-sm overflow-hidden">
          <button type="button" onClick={dec} disabled={qty <= 1 || submitting || isPendingRemove} className="w-8 h-8 flex items-center justify-center bg-white active:bg-secondary disabled:opacity-30" data-testid={`qty-dec-${batch.id}`} aria-label="Decrease">
            <Minus size={14} strokeWidth={2.5} />
          </button>
          <button type="button" onClick={setAll} className="w-10 h-8 font-mono text-xs font-bold text-center bg-white active:bg-secondary border-x border-ink/15" data-testid={`qty-display-${batch.id}`} title="Tap to set to all" aria-label="Set to full quantity">
            {qty}
          </button>
          <button type="button" onClick={inc} disabled={qty >= batch.quantity || submitting || isPendingRemove} className="w-8 h-8 flex items-center justify-center bg-white active:bg-secondary disabled:opacity-30" data-testid={`qty-inc-${batch.id}`} aria-label="Increase">
            <Plus size={14} strokeWidth={2.5} />
          </button>
        </div>
        <button type="button" onClick={doRemove} disabled={submitting || isPendingRemove || isPendingAdd} className="h-8 px-3 border border-ink/70 bg-white text-ink hover:bg-ink hover:text-paper active:bg-ink active:text-paper font-display text-[11px] uppercase tracking-wider rounded-sm transition-colors disabled:opacity-50 flex items-center gap-1" data-testid={`remove-button-${batch.id}`}>
          <Trash2 size={12} strokeWidth={2.5} />
          {submitting ? "…" : isPendingRemove ? "Queued" : "Remove"}
        </button>
      </div>
    </div>
  );
}

function StatusGroupHeader({ urgency, total, shown }) {
  const m = URGENCY_META[urgency];
  if (!m) return null;
  const hidden = total - shown;
  return (
    <div className={`flex items-center gap-3 px-3 py-2 ${m.bg}`} data-testid={`group-header-${urgency}`}>
      <div className={`w-1.5 self-stretch ${m.bar}`} />
      <div className="flex-1 min-w-0">
        <div className={`font-display font-black text-xs uppercase tracking-wide leading-none ${m.text}`}>{m.label}</div>
        <div className="font-mono text-[10px] uppercase tracking-wider opacity-60 mt-0.5">{m.hint}</div>
      </div>
      <div className={`font-mono text-sm font-black tabular-nums ${m.text}`} data-testid={`group-count-${urgency}`}>
        {total}
      </div>
      {hidden > 0 && (
        <div className="font-mono text-[10px] uppercase tracking-wider opacity-60" data-testid={`group-hidden-${urgency}`}>
          showing {shown}
        </div>
      )}
    </div>
  );
}

function ConfirmModal({ count, onConfirm, onCancel, working }) {
  if (count <= 0) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3" data-testid="bulk-confirm-modal" onClick={onCancel}>
      <div className="bg-white border-2 border-ink rounded-sm w-full max-w-sm shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-4 border-b border-border">
          <AlertTriangle size={20} className="text-shelf-urgent" />
          <div className="font-display font-black text-lg leading-none">Remove {count} {count === 1 ? "batch" : "batches"}?</div>
          <button onClick={onCancel} className="ml-auto p-1" data-testid="bulk-confirm-close" aria-label="Cancel"><X size={18} /></button>
        </div>
        <div className="p-4 text-sm">
          Each selected batch will be fully removed from inventory and recorded on the Removals tab.
          This cannot be undone.
        </div>
        <div className="p-3 flex gap-2 border-t border-border">
          <button onClick={onCancel} disabled={working} className="flex-1 h-10 border border-ink/70 font-display text-xs uppercase tracking-wider rounded-sm hover:bg-secondary" data-testid="bulk-confirm-cancel">Cancel</button>
          <button onClick={onConfirm} disabled={working} className="flex-1 h-10 bg-ink text-paper font-display text-xs uppercase tracking-wider rounded-sm hover:bg-brand transition-colors disabled:opacity-50" data-testid="bulk-confirm-yes">
            {working ? "Removing…" : "Remove all"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InventoryPage() {
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [outboxEntries, setOutboxEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkWorking, setBulkWorking] = useState(false);
  const [usingCached, setUsingCached] = useState(false);
  const [cacheFetchedAt, setCacheFetchedAt] = useState(null);
  // True when the device is offline AND no local snapshot exists. The
  // UI must render an "unavailable offline" surface instead of a fake
  // "0 batches" empty state.
  const [unavailableOffline, setUnavailableOffline] = useState(false);

  const refreshOutbox = async () => {
    if (!user || !user.id) { setOutboxEntries([]); return; }
    const all = await listOutbox(user.id);
    setOutboxEntries(all.filter((e) => e.kind === "add_inventory" || e.kind === "remove_inventory"));
  };

  const load = async ({ allowCacheFallback = true } = {}) => {
    if (!user || !user.id) { setLoading(false); return; }
    // Try the network first.
    try {
      const { data } = await api.get("/inventory");
      setBatches(data);
      setUsingCached(false);
      setUnavailableOffline(false);
      setCacheFetchedAt(null);
      setSelected(new Set());
      saveInventory(user.id, data).catch(() => {});
    } catch (e) {
      // Network failure. There are two regimes:
      //   - Cold load (no in-memory state) → fall back to the IndexedDB
      //     snapshot so the page isn't blank.
      //   - Refresh AFTER a user action (allowCacheFallback=false) → keep
      //     the in-memory state we already have. The cache snapshot is by
      //     definition older than the action the user just took, so
      //     replacing live state with it would resurrect rows the user
      //     thought they had removed (the exact UX bug we're fixing).
      if (!allowCacheFallback) {
        toast.warning(
          "Couldn't refresh inventory — your removal saved on the server " +
          "but the list may be a moment behind. Pull to refresh.",
          { duration: 6000 }
        );
        return;
      }
      const cached = await readInventory(user.id);
      if (cached && Array.isArray(cached.items)) {
        setBatches(cached.items);
        setUsingCached(true);
        setCacheFetchedAt(cached.fetchedAt || null);
        setUnavailableOffline(false);
        // Silent — the inline "Showing cached data" banner is the signal.
      } else if (isNetworkError(e)) {
        // Offline & no cache → render the unavailable surface; suppress
        // the red toast. NEVER show "no items yet" in this state.
        setBatches([]);
        setUsingCached(false);
        setUnavailableOffline(true);
      } else if (e?.response) {
        toast.error(formatApiErrorDetail(e));
      }
    } finally {
      setLoading(false);
    }
    await refreshOutbox();
  };

  // Reload helper used immediately after a successful remove/add. Retries
  // once after 300 ms on transient network failure before giving up on the
  // refresh — most Android Chrome blips during a 4G/5G handoff are well
  // under that. After both attempts, we never fall back to the cache
  // snapshot (it predates the action and would resurrect removed rows).
  const reloadAfterAction = async () => {
    try {
      const { data } = await api.get("/inventory");
      setBatches(data);
      setUsingCached(false);
      setSelected(new Set());
      saveInventory(user.id, data).catch(() => {});
      await refreshOutbox();
      return;
    } catch (e) {
      if (e?.response) {
        // Server replied with an error code — bubble it up immediately;
        // a network blip is not the cause.
        toast.error(formatApiErrorDetail(e));
        await refreshOutbox();
        return;
      }
      // No response → transient. One short retry.
    }
    await new Promise((r) => setTimeout(r, 300));
    await load({ allowCacheFallback: false });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Re-pull outbox every time the sync runner emits — keeps the row
  // overlays accurate as entries sync (or fail).
  useEffect(() => {
    return syncRunner.subscribe(() => {
      refreshOutbox().catch(() => {});
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.id]);

  // Merge in queued add/remove actions as "pending" overlays. Removes
  // mark the existing batch; adds inject a synthetic row at the top.
  const effectiveBatches = useMemo(() => {
    if (!outboxEntries.length) return batches;
    const pendingRemovesById = new Map();
    const pendingAdds = [];
    for (const e of outboxEntries) {
      if (e.kind === "remove_inventory" && e.targetId) {
        pendingRemovesById.set(e.targetId, e);
      } else if (e.kind === "add_inventory") {
        const body = e.body || {};
        pendingAdds.push({
          id: `pending:${e.id}`,
          product_id: body.product_id,
          product_name: body.product_name_hint || "Pending item",
          product_image_url: null,
          quantity: body.quantity || 1,
          expiration_date: body.expiration_date,
          urgency: "fresh",
          _pendingAdd: true,
          _syncError: e.status === "error",
        });
      }
    }
    const overlaid = batches.map((b) => {
      const ent = pendingRemovesById.get(b.id);
      if (!ent) return b;
      return { ...b, _pendingRemove: true, _syncError: ent.status === "error" };
    });
    return [...pendingAdds, ...overlaid];
  }, [batches, outboxEntries]);

  // The rest of the page reads `effectiveBatches` instead of raw `batches`.

  // Group all batches by urgency for filtering + per-status totals.
  const groupedAll = useMemo(() => {
    const g = { expired: [], urgent: [], attention: [], fresh: [] };
    for (const b of effectiveBatches) if (g[b.urgency]) g[b.urgency].push(b);
    return g;
  }, [effectiveBatches]);

  // visibleGroups: ordered [{urgency, items, total}] — capped only on "All".
  const visibleGroups = useMemo(() => {
    const buckets = filter === "all" ? URGENCY_ORDER : [filter];
    const cap = filter === "all" ? ALL_VIEW_LIMIT_PER_STATUS : Infinity;
    return buckets
      .filter((u) => URGENCY_META[u])
      .map((u) => {
        const all = groupedAll[u] || [];
        return { urgency: u, items: all.slice(0, cap), total: all.length };
      });
  }, [groupedAll, filter]);

  // ids of every batch currently visible after filter + per-status slice.
  // Exclude pending (synthetic) and rows queued for removal — bulk
  // operations only apply to real, sync-clean rows.
  const visibleIds = useMemo(() => {
    const ids = [];
    for (const g of visibleGroups) {
      for (const b of g.items) {
        if (!b._pendingAdd && !b._pendingRemove) ids.push(b.id);
      }
    }
    return ids;
  }, [visibleGroups]);

  const visibleCount = visibleIds.length;
  const allVisibleSelected = visibleCount > 0 && visibleIds.every((id) => selected.has(id));

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  // Map an id -> batch + reason for the bulk POST step.
  const batchById = useMemo(() => {
    const m = new Map();
    for (const b of effectiveBatches) m.set(b.id, b);
    return m;
  }, [effectiveBatches]);

  /**
   * Apply a removal optimistically to the in-memory list AND to the
   * IndexedDB snapshot. Called the moment the server confirms a remove
   * (or a bulk remove) so the cache can never resurrect a row that the
   * user already removed — even if the immediate refresh GET fails.
   */
  const applyLocalRemove = (batchId, removedQty) => {
    setBatches((prev) => {
      const next = prev
        .map((b) => {
          if (b.id !== batchId) return b;
          const left = Math.max(0, (Number(b.quantity) || 0) - Number(removedQty));
          if (left === 0) return null;  // fully removed → drop the row
          return { ...b, quantity: left };
        })
        .filter(Boolean);
      // Persist the patched snapshot so a subsequent cold-load shows the
      // up-to-date list. Best-effort; never block UI.
      if (user && user.id) saveInventory(user.id, next).catch(() => {});
      return next;
    });
  };

  /** Enqueue a single remove for a row (used by both single + bulk paths). */
  const enqueueRemove = async (batchId, body) => {
    if (!user || !user.id) return;
    // De-dupe: if there's already a pending remove for this batch, skip.
    const dup = outboxEntries.find((e) => e.kind === "remove_inventory" && e.targetId === batchId && e.status !== "error");
    if (dup) return;
    await enqueueOutbox(user.id, {
      kind: "remove_inventory",
      url: `/inventory/${batchId}/remove`,
      body,
      targetId: batchId,
    });
    syncRunner.kick("inventory-remove");
    await refreshOutbox();
  };

  const runBulkRemove = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkWorking(true);
    let ok = 0; let fail = 0; let queued = 0;
    const offline = !isOnline();
    for (const id of ids) {
      const b = batchById.get(id);
      if (!b) { fail++; continue; }
      const reason = (URGENCY_META[b.urgency] || URGENCY_META.fresh).defaultReason;
      const body = { quantity: b.quantity, reason, note: "bulk-remove" };
      if (offline) {
        await enqueueRemove(id, body);
        queued++;
        continue;
      }
      try {
        await api.post(`/inventory/${id}/remove`, body);
        ok++;
        // Same cache-patch as the single-row flow.
        try { applyLocalRemove(id, b.quantity); } catch (_) { /* never block */ }
      } catch (e) {
        if (!e?.response) { await enqueueRemove(id, body); queued++; }
        else fail++;
      }
    }
    setBulkWorking(false);
    setConfirmOpen(false);
    if (ok) toast.success(`Removed ${ok} ${ok === 1 ? "batch" : "batches"}`);
    if (queued) toast.success(`Queued ${queued} for sync`);
    if (fail) toast.error(`${fail} batch${fail === 1 ? "" : "es"} failed to remove`);
    await reloadAfterAction();
  };

  const total = effectiveBatches.length;
  const selectedCount = selected.size;

  return (
    <div className="px-4 pt-6 pb-6" data-testid="inventory-page">
      <div className="flex items-baseline justify-between mb-1">
        <div className="data-sku">Current stock</div>
        {!unavailableOffline && (
          <div className="font-mono text-xs text-muted-foreground tabular-nums">{total} batches</div>
        )}
      </div>
      <h1 className="font-display font-black text-3xl sm:text-4xl leading-none tracking-tighter mb-4">
        Inventory
      </h1>

      {usingCached && !unavailableOffline && (
        <CachedDataNotice
          fetchedAt={cacheFetchedAt}
          label="Showing cached inventory"
          data-testid="inventory-cached-banner"
        />
      )}

      {!loading && unavailableOffline && (
        <OfflineUnavailable
          title="Inventory unavailable offline"
          description="This device does not currently have a local inventory snapshot. Reconnect to refresh inventory."
          onRetry={() => load()}
          data-testid="inventory-offline-unavailable"
        />
      )}

      {/* Filter + select-all toolbar */}
      {!unavailableOffline && (
      <div className="card-flat px-3 py-2 flex items-center gap-2 mb-3" data-testid="inventory-toolbar">
        <input
          type="checkbox"
          checked={allVisibleSelected}
          onChange={toggleSelectAllVisible}
          disabled={visibleCount === 0}
          className="w-4 h-4 accent-brand cursor-pointer shrink-0"
          data-testid="select-all-visible"
          aria-label="Select all visible"
        />
        <select
          value={filter}
          onChange={(e) => { setFilter(e.target.value); }}
          className="h-9 px-2 border border-border rounded-sm bg-white font-mono text-xs uppercase tracking-wider"
          data-testid="inventory-filter-select"
        >
          {FILTERS.map((f) => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>
        <div className="flex-1 font-mono text-[11px] text-muted-foreground tabular-nums truncate" data-testid="inventory-toolbar-counts">
          {selectedCount > 0 ? `${selectedCount} selected` : `${visibleCount} shown`}
        </div>
        {selectedCount > 0 && (
          <>
            <button
              onClick={clearSelection}
              className="h-9 px-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:text-ink"
              data-testid="bulk-clear-selection"
            >Clear</button>
            <button
              onClick={() => setConfirmOpen(true)}
              className="h-9 px-3 bg-ink text-paper font-display text-[11px] uppercase tracking-wider rounded-sm hover:bg-brand transition-colors flex items-center gap-1"
              data-testid="bulk-remove-button"
            >
              <Trash2 size={12} strokeWidth={2.5} />
              Remove {selectedCount}
            </button>
          </>
        )}
      </div>
      )}

      {loading && <div className="card-flat p-4 data-sku">Loading…</div>}

      {!loading && !unavailableOffline && total === 0 && (
        <div className="card-flat p-10 text-center" data-testid="empty-inventory">
          <div className="data-sku mb-2">Empty</div>
          <div className="font-display text-lg">No items in stock yet.</div>
          <div className="text-xs text-muted-foreground mt-2">Scan a barcode from the Scan tab to add a batch.</div>
        </div>
      )}

      {!loading && !unavailableOffline && total > 0 && (
        <div className="card-flat overflow-hidden" data-testid="inventory-list">
          {visibleGroups.every((g) => g.total === 0) ? (
            <div className="px-4 py-8 text-center font-mono text-[11px] uppercase tracking-wider text-muted-foreground" data-testid="inventory-filter-empty">
              No items in this status.
            </div>
          ) : (
            visibleGroups.map((g) => (
              g.total === 0 ? null : (
                <div key={g.urgency}>
                  <StatusGroupHeader urgency={g.urgency} total={g.total} shown={g.items.length} />
                  {g.items.map((b) => (
                    <InventoryRow
                      key={b.id}
                      batch={b}
                      selected={selected.has(b.id)}
                      onToggle={toggleOne}
                      onRemoved={reloadAfterAction}
                      onEnqueueRemove={enqueueRemove}
                      onLocalApplyRemove={applyLocalRemove}
                    />
                  ))}
                </div>
              )
            ))
          )}
        </div>
      )}

      {confirmOpen && (
        <ConfirmModal
          count={selectedCount}
          working={bulkWorking}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={runBulkRemove}
        />
      )}
    </div>
  );
}
